<template>
  <v-card v-if="$isVihkoAdmin">
    <v-card-title>
      <v-icon left>mdi-clipboard-list-outline</v-icon>
      Siisteyden tarkastuslistan asetukset
      <v-spacer />
      <v-btn icon @click="expanded = !expanded">
        <v-icon>{{ expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </v-btn>
    </v-card-title>
    <template v-if="expanded">
      <v-divider />
      <v-card-text>
        <v-progress-linear v-if="loading" indeterminate />
        <div class="text-caption mb-2">
          Raahaa rivejä <v-icon small>mdi-drag</v-icon> -kuvakkeesta
          järjestääksesi ne uudelleen. Muutokset näkyvät uusissa merkinnöissä.
          Jo tallennettujen merkintöjen vastaukset säilyvät ennallaan.
        </div>
        <v-list dense class="grey lighten-5 rounded mb-4">
          <v-list-item
            v-for="(item, index) in items"
            :key="item.id"
            :class="{ 'grey lighten-4': draggedIndex === index }"
            draggable="true"
            @dragstart="onDragStart(index, $event)"
            @dragover.prevent
            @dragenter.prevent="onDragEnter(index)"
            @dragend="onDragEnd"
          >
            <v-list-item-avatar
              class="ma-0 mr-2 align-self-center"
              style="cursor: move"
              title="Raahaa järjestääksesi"
            >
              <v-icon>mdi-drag</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-row dense align="center">
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="item.title"
                    :class="item.type === 'title' && 'font-weight-bold'"
                    :label="item.type === 'title' ? 'Väliotsikko' : 'Kohta'"
                    background-color="white"
                    dense
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col cols="12" sm="4" class="d-flex justify-end">
                  <v-btn
                    icon
                    small
                    :color="item.type === 'title' ? 'primary' : ''"
                    :title="
                      item.type === 'title'
                        ? 'Muuta tarkastettavaksi kohdaksi'
                        : 'Muuta väliotsikoksi'
                    "
                    @click="toggleType(index)"
                  >
                    <v-icon>mdi-format-header-2</v-icon>
                  </v-btn>
                  <v-btn icon small color="error" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-btn class="mr-2" small text @click="addItem(null)">
          <v-icon left>mdi-plus</v-icon>
          Lisää kohta
        </v-btn>
        <v-btn class="mr-2" small text @click="addItem('title')">
          <v-icon left>mdi-plus</v-icon>
          Lisää väliotsikko
        </v-btn>
        <v-btn small text @click="importDialog = true">
          <v-icon left>mdi-import</v-icon>
          Tuo tekstistä
        </v-btn>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn :loading="saving" color="primary" text @click="save">
          Tallenna
        </v-btn>
      </v-card-actions>
    </template>

    <v-dialog
      v-model="importDialog"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="700px"
      persistent
    >
      <v-card>
        <v-card-title>Tuo tarkastuslista tekstistä</v-card-title>
        <v-divider />
        <v-card-text>
          <div class="text-caption mb-2">
            Liitä lista alle. Rivit jotka alkavat merkillä
            <strong>-</strong> tulkitaan tarkastettaviksi kohdiksi, muut rivit
            väliotsikoiksi. Tyhjät rivit ohitetaan.
          </div>
          <v-textarea
            v-model="importText"
            auto-grow
            outlined
            rows="12"
            placeholder="Koulutusrakennus - Keittiö&#10;- Jääkaappi, tarkistus ja tyhjennys&#10;- Lattian siisteys / imurointi"
          />
          <div v-if="importPreview.length" class="text-caption">
            Tunnistettu {{ importPreview.length }} riviä:
            {{ importPreview.filter((i) => i.type === "title").length }}
            väliotsikkoa ja
            {{ importPreview.filter((i) => i.type !== "title").length }}
            tarkastettavaa kohtaa.
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn color="error" text @click="importDialog = false"
            >Peruuta</v-btn
          >
          <v-spacer />
          <v-btn
            :disabled="!importPreview.length"
            text
            @click="applyImport(false)"
            >Lisää perään
          </v-btn>
          <v-btn
            :disabled="!importPreview.length"
            color="primary"
            text
            @click="applyImport(true)"
            >Korvaa lista
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { doc, getDoc, setDoc } from "firebase/firestore";
import { firestore as db } from "@/plugins/firebase.app";

export default {
  name: "VihkoTarkastusAsetukset",
  data: () => ({
    expanded: false,
    loading: false,
    saving: false,
    items: [],
    draggedIndex: null,
    importDialog: false,
    importText: "",
  }),
  computed: {
    importPreview() {
      return this.importText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) =>
          line.startsWith("-")
            ? { title: line.replace(/^-+\s*/, ""), type: null }
            : { title: line, type: "title" },
        )
        .filter((item) => item.title.length > 0);
    },
  },
  created() {
    if (this.$isVihkoAdmin) this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const snapshot = await getDoc(
          doc(db, "leirikoski_vihko_settings", "siisteystarkastus"),
        );
        this.items = snapshot.exists() ? snapshot.data().items || [] : [];
      } catch (e) {
        console.error(e);
        this.$toast("Tarkastuslistan haku epäonnistui", { type: "error" });
      }
      this.loading = false;
    },
    addItem(type) {
      this.items.push({ id: this.$uuidv4(), title: "", type });
    },
    // Rows already on the list keep their id so answers in saved vihko entries stay attached.
    applyImport(replace) {
      const used = new Set();
      const imported = this.importPreview.map((row) => {
        const match = this.items.find(
          (i) =>
            !used.has(i.id) &&
            i.title === row.title &&
            (i.type || null) === row.type,
        );
        if (match) {
          used.add(match.id);
          return { ...match, title: row.title, type: row.type };
        }
        return { id: this.$uuidv4(), title: row.title, type: row.type };
      });

      if (replace) {
        this.items = imported;
      } else {
        this.items = [
          ...this.items,
          ...imported.filter((row) => !used.has(row.id)),
        ];
      }
      this.importDialog = false;
      this.importText = "";
      this.$toast("Lista tuotu. Muista tallentaa.", { type: "success" });
    },
    toggleType(index) {
      this.items[index].type =
        this.items[index].type === "title" ? null : "title";
    },
    async removeItem(index) {
      if (!(await this.$deleteConfirm("Haluatko varmasti poistaa rivin?"))) {
        return;
      }
      this.items.splice(index, 1);
    },
    onDragStart(index, event) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.dropEffect = "move";
    },
    onDragEnter(index) {
      if (this.draggedIndex !== null && this.draggedIndex !== index) {
        const item = this.items.splice(this.draggedIndex, 1)[0];
        this.items.splice(index, 0, item);
        this.draggedIndex = index;
      }
    },
    onDragEnd() {
      this.draggedIndex = null;
    },
    async save() {
      this.saving = true;
      try {
        await setDoc(
          doc(db, "leirikoski_vihko_settings", "siisteystarkastus"),
          {
            items: this.items.filter((i) => (i.title || "").trim()),
            updatedAt: Date.now(),
            updatedBy: this.$userDBContext,
          },
        );
        this.$toast("Tarkastuslista tallennettu", { type: "success" });
        await this.load();
      } catch (e) {
        console.error(e);
        this.$toast("Tarkastuslistan tallennus epäonnistui", { type: "error" });
      }
      this.saving = false;
    },
  },
};
</script>
